<script>
import Vue from 'vue';
import Breadcrumbs from '@/components/file-browser/Breadcrumbs';
import FileList from '@/components/file-list/FileList';
import getErrorMessage from '@/modules/server-errors';
import { extname } from '@/modules/file-utilities';
import { prettyFilename } from '@/modules/prettifier';

export default {
  name: 'EnablePagesDialog',
  components: {
    Breadcrumbs,
    FileList
  },
  props: {
    site: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      basePath: '',
      currentPath: '',
      dialogVisible: false,
      errorMessage: '',
      files: [],
      isLoading: false
    };
  },
  methods: {
    open() {
      this.files = [];
      this.basePath = this.site.homeFolder;
      this.currentPath = this.site.homeFolder;
      this.dialogVisible = true;
      this.getFiles(this.currentPath);
    },

    close() {
      this.dialogVisible = false;
    },

    getFiles(path) {
      // Make sure the path is inside the upload folder
      this.currentPath = this.isUnderBasePath(path) ? path : this.basePath;
      this.isLoading = true;
      this.$emit('path-change', this.currentPath);
      this.$http.get(`/sites/${this.site.id}/files`, {
        params: {
          path: this.currentPath,
          showEnabled: true
        }
      })
        .then(res => {
          this.files = res.data.files.map(file => {
            return Object.assign(file, {
              isDisabled: !this.canBeEnabled(file.name)
            });
          });
          this.fileSelection = [];
          this.$refs.fileList.scrollToTop();
        })
        .catch(err => this.errorMessage = getErrorMessage(err.response.data.code))
        .finally(() => this.isLoading = false);
    },

    canBeEnabled(filename) {
      const extension = extname(filename).toLowerCase();
      //
      // TODO: This list is copied from: surreal-server/modules/database/models/page.js
      // It would make sense to find a better way to share it between the two projects.
      //
      return [
        '.asp', '.aspx', '.ascx', '.cshtml', '.ctp', '.dwt', '.htm', '.html', '.inc', '.jps',
        '.lasso', '.master', '.phtml', '.php', '.py', '.rb', '.shtm', '.shtml', '.tpl'
      ].includes(extension);
    },

    isUnderBasePath(path) {
      path = path || '';
      return path.substring(0, this.basePath.length) === this.basePath;
    },

    enablePage(file) {
      const path = file.path.substring(this.site.homeFolder.length).replace(/^\//g, '');
      const isIndex = !!path.match(/^(index|default)\./);
      const isPinned = isIndex;
      const createOnServer = false;
      const name = isIndex ? this.$t('Homepage') : prettyFilename(file.name);

      if (file.type === 'file' && !file.isEnabled) {
        Vue.set(file, 'isBusy', true);
        this.$http.post(`/sites/${this.site.id}/pages`, {
          name,
          path,
          createOnServer,
          isPinned
        })
          .then(res => {
            Vue.set(file, 'isEnabled', true);
            this.$emit('page-enabled', res.data.page);
          })
          .catch(err => {
            this.$notify({
              type: 'warning',
              title: file.name,
              message: getErrorMessage(err.response.data.code)
            });
          })
          .finally(() => Vue.set(file, 'isBusy', false));
      }
    }
  }
};
</script>

<template>
  <el-dialog
    :title="$t('Enable Pages')"
    :visible.sync="dialogVisible"
  >
    <!-- Breadcrumbs -->
    <breadcrumbs
      :path="currentPath"
      :base-path="site.homeFolder"
      class="breadcrumbs"
      @path-change="getFiles"
    />

    <!-- Files -->
    <file-list
      ref="fileList"
      v-model="files"
      :can-select="() => false"
      :error-text="errorMessage"
      :has-error="errorMessage.length > 0"
      :loading="isLoading"
      :filter-method="file => !isUnderBasePath(file.path) && file.path !== '..'"
      @click="enablePage"
      @path-change="getFiles"
    />

    <span slot="footer" class="dialog-footer">
      <el-button type="primary" @click="close">
        {{ $t('Done') }}
      </el-button>
    </span>
  </el-dialog>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

// No styles yet

</style>
